import {
  Box,
  VStack,
  Text,
  Link,
  useColorModeValue,
  useDisclosure,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from '@chakra-ui/react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import {
  FiMenu,
  FiHome,
  FiPlay,
  FiUsers,
  FiCalendar,
  FiBox,
  FiUsers as FiBrigades,
  FiSettings,
  FiAlertTriangle,
  FiPackage,
  FiCompass,
  FiCode,
  FiShield,
  FiTool,
  FiLayers,
  FiBell,
} from 'react-icons/fi'

type MenuItem = { path: string; label: string; icon: React.ComponentType<{ size?: string | number }> }
type MenuSection = { title?: string; items: MenuItem[] }

const menuSections: MenuSection[] = [
  {
    items: [
      { path: '/', label: 'Главная', icon: FiHome },
      { path: '/getting-started', label: 'Начало работы', icon: FiPlay },
      { path: '/schedule', label: 'График работ', icon: FiCalendar },
      { path: '/objects-and-blocks', label: 'Объекты и блоки', icon: FiBox },
      { path: '/brigades', label: 'Бригады', icon: FiBrigades },
      { path: '/warehouse', label: 'Склад', icon: FiPackage },
      { path: '/users-and-roles', label: 'Роли и доступы', icon: FiUsers },
      { path: '/role-scenarios', label: 'Сценарии по ролям', icon: FiCompass },
      { path: '/settings', label: 'Настройки и отчёты', icon: FiSettings },
      { path: '/troubleshooting', label: 'Частые вопросы', icon: FiAlertTriangle },
    ],
  },
  {
    title: 'API',
    items: [
      { path: '/api', label: 'Обзор API', icon: FiCode },
      { path: '/api/access-org', label: 'Доступ и организация', icon: FiShield },
      { path: '/api/production', label: 'Производственный контур', icon: FiTool },
      { path: '/api/warehouse', label: 'Складской контур', icon: FiLayers },
      { path: '/api/platform', label: 'Платформа и интеграции', icon: FiBell },
    ],
  },
]

export const Sidebar: React.FC = () => {
  const location = useLocation()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const activeBgColor = useColorModeValue('brand.50', 'brand.900')
  const activeColor = useColorModeValue('brand.700', 'brand.100')

  const SidebarContent = () => (
    <VStack spacing={3} align="stretch" w="full">
      {menuSections.map((section, sectionIdx) => (
        <Box key={`section-${sectionIdx}`}>
          {section.title ? (
            <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="0.08em" px={2}>
              {section.title}
            </Text>
          ) : null}
          <VStack spacing={1} align="stretch">
            {section.items.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path

              return (
                <Link
                  key={item.path}
                  as={RouterLink}
                  to={item.path}
                  display="flex"
                  alignItems="center"
                  gap={3}
                  px={3}
                  py={2.5}
                  borderRadius="12px"
                  color={isActive ? activeColor : 'gray.700'}
                  bg={isActive ? activeBgColor : 'transparent'}
                  border="1px solid"
                  borderColor={isActive ? 'brand.100' : 'transparent'}
                  _hover={{
                    bg: useColorModeValue('gray.50', 'gray.700'),
                    textDecoration: 'none',
                  }}
                  transition="all 0.2s"
                >
                  <Icon size={17} />
                  <Text fontSize="sm" fontWeight={isActive ? 'semibold' : 'medium'} noOfLines={1}>
                    {item.label}
                  </Text>
                </Link>
              )
            })}
          </VStack>
        </Box>
      ))}
    </VStack>
  )

  return (
    <>
      <IconButton
        display={{ base: 'flex', lg: 'none' }}
        onClick={onOpen}
        icon={<FiMenu />}
        aria-label="Открыть меню"
        position="fixed"
        top={20}
        left={4}
        zIndex={20}
        colorScheme="brand"
        size="sm"
        borderRadius="12px"
      />

      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Разделы</DrawerHeader>
          <DrawerBody p={3}>
            <SidebarContent />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Box
        display={{ base: 'none', lg: 'block' }}
        position="fixed"
        left={0}
        top={16}
        w={72}
        h="calc(100vh - 64px)"
        bg={bgColor}
        borderRight="1px"
        borderColor={borderColor}
        overflowY="auto"
        zIndex={10}
      >
        <Box p={4}>
          <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={3} textTransform="uppercase" letterSpacing="0.08em">
            Содержание
          </Text>
          <SidebarContent />
        </Box>
      </Box>
    </>
  )
}
